<template>
  <TableBasicTextLoader
    :table="tableTransformed"
    :sortBy="table.sort ? sortBy(table.sort?.col, table.sort?.field) : undefined"
    :direction="table.sort?.direction"
    :caption="table.caption?.[locale] ?? ''"
  />
</template>
<script lang="ts" setup>
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import TableBasicTextLoader from '@/design-system/atoms/TableBasicTextLoader.vue'
import { computed, toRefs } from 'vue'
import { useDataSources } from '@/composables/useDataSources'
import { useI18n } from 'vue-i18n'
import { FormatOptions, formatter } from '../formatter'

export type Cell =
  | { type: 'data-source'; dataSource: string; format?: FormatOptions }
  | { type: 'text'; text: LocaleString }
  | { type: 'number'; number?: number; isLoading?: boolean; format?: FormatOptions }

export type Table = {
  sort?: { col: number; direction: 'asc' | 'desc'; field: string }
  caption?: LocaleString
  headers: LocaleString[]
  rows: Cell[][]
}

type Props = {
  table: Table
}

const props = defineProps<Props>()

const { locale } = useI18n()
const { dataSources } = toRefs(useDataSources())

const sortBy =
  (col: number, field: string) =>
  (i: number): string => {
    return props.table.rows?.[i]?.[col]?.[field] ?? '?'
  }

const tableTransformed = computed(() => {
  return {
    headers: props.table.headers.map((header) => {
      return {
        text: header[locale.value]
      }
    }),
    rows: props.table.rows.map((row) => {
      return row.map((cell) => {
        switch (cell.type) {
          case 'text':
            return {
              text: cell.text[locale.value]
            }
          case 'number':
            return {
              text: formatter(cell.number, locale.value, cell.format),
              isLoading: cell.isLoading ?? true
            }
          case 'data-source':
            return {
              text: formatter(
                dataSources.value[cell.dataSource]?.data as number | string,
                locale.value,
                cell.format
              ),
              isLoading: dataSources.value[cell.dataSource]?.isLoading ?? true
            }
        }
      })
    })
  }
})
</script>
